import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Switch,
  Alert,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import { useSession } from '../../permissions/SessionContext';
import { hasPermissions } from '../../permissions/check';
import { fetchClasses, createClass, updateClass } from '../../api/academy';
import { academyScope } from './scope';
import { colors } from '../../design-system/tokens';

const won = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

const capacityStatus = (row) => {
  const capacity = Number(row?.capacity || 0);
  const studentCount = Number(row?.studentCount || 0);
  if (!capacity || studentCount < capacity) return null;
  return {
    label: studentCount === capacity ? '정원 도달 · 배정 허용' : '정원 초과 · 배정 허용',
    severity: studentCount === capacity ? 'warning' : 'error',
  };
};

const emptyForm = {
  name: '',
  tuitionFee: '',
  scheduleText: '',
  teacherName: '',
  capacity: '',
};

const Classes = () => {
  const { session } = useSession();
  // 조회 권한만 있는 직원에게는 등록/수정 UI를 감춘다 (실제 차단은 backend classes.manage)
  const canManage = hasPermissions(session, ['classes.manage']);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await fetchClasses({ includeInactive: true, ...academyScope(session) }));
    } catch (e) {
      setError(e?.response?.data?.message || '반 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name ?? '',
      tuitionFee: String(row.tuitionFee ?? ''),
      scheduleText: row.scheduleText ?? '',
      teacherName: row.teacherName ?? '',
      capacity: row.capacity ? String(row.capacity) : '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name: form.name,
        tuitionFee: Number(form.tuitionFee),
        scheduleText: form.scheduleText,
        teacherName: form.teacherName,
        capacity: form.capacity ? Number(form.capacity) : 0,
      };
      const scope = academyScope(session);
      if (editing) await updateClass(editing._id, { ...payload, ...scope });
      else await createClass({ ...payload, ...scope });
      setDialogOpen(false);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.message || '저장하지 못했어요.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await updateClass(row._id, { isActive: !row.isActive, ...academyScope(session) });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || '상태를 바꾸지 못했어요.');
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <PageHeader
          title="수업 관리"
          description="반과 월 교습비를 관리해요. 교습비를 바꿔도 기존 수강생의 청구 금액은 등록 시점 금액으로 유지됩니다. 정원에 도달하면 경고를 보이지만 원생 배정은 허용하며 대기 명단은 만들지 않습니다."
        />
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            반 추가
          </Button>
        )}
      </Box>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyText="아직 등록된 반이 없어요. 반을 추가한 뒤 수강생을 등록·배정할 수 있어요."
      >
        <Paper sx={{ p: 1 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>반 이름</TableCell>
                  <TableCell>일정</TableCell>
                  <TableCell>담당</TableCell>
                  <TableCell align="right">월 교습비</TableCell>
                  <TableCell align="right">재원 / 운영 정원</TableCell>
                  <TableCell align="center">운영</TableCell>
                  {canManage && <TableCell align="right">관리</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
                      {row.scheduleText || '-'}
                    </TableCell>
                    <TableCell sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
                      {row.teacherName || '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {won(row.tuitionFee)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <span>
                          {row.studentCount}
                          {row.capacity ? ` / ${row.capacity}` : ''}
                        </span>
                        {capacityStatus(row) && (
                          <Chip
                            size="small"
                            color={capacityStatus(row).severity}
                            label={capacityStatus(row).label}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {canManage ? (
                        <Switch
                          size="small"
                          checked={row.isActive}
                          onChange={() => toggleActive(row)}
                          slotProps={{ input: { 'aria-label': `${row.name} 운영 여부` } }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={row.isActive ? '운영' : '중단'}
                          sx={{
                            bgcolor: row.isActive
                              ? colors.status.success.bg
                              : colors.status.neutral.bg,
                            color: row.isActive
                              ? colors.status.success.fg
                              : colors.status.neutral.fg,
                          }}
                        />
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Button size="small" onClick={() => openEdit(row)}>
                          수정
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DataState>

      {!loading && !error && rows.length === 0 && canManage && (
        <Alert severity="info" sx={{ mt: 2 }}>
          반 이름과 수업 일정, 참고 정원을 먼저 입력하면 원생 배정과 출결 기록을 이어갈 수 있어요.
          <Button size="small" onClick={openCreate} sx={{ ml: 1 }}>
            반 추가
          </Button>
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '반 수정' : '반 추가'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="반 이름"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="중2 수학 A반"
            fullWidth
            autoFocus
          />
          <TextField
            label="월 교습비 (원)"
            value={form.tuitionFee}
            onChange={(e) =>
              setForm({ ...form, tuitionFee: e.target.value.replace(/[^0-9]/g, '') })
            }
            inputMode="numeric"
            fullWidth
          />
          <TextField
            label="수업 일정"
            value={form.scheduleText}
            onChange={(e) => setForm({ ...form, scheduleText: e.target.value })}
            placeholder="월·수·금 16:00~18:00"
            fullWidth
          />
          <TextField
            label="담당 강사"
            value={form.teacherName}
            onChange={(e) => setForm({ ...form, teacherName: e.target.value })}
            fullWidth
          />
          <TextField
            label="운영 정원 · 경고 기준 (선택)"
            value={form.capacity}
            onChange={(e) =>
              setForm({ ...form, capacity: e.target.value.replace(/[^0-9]/g, '') })
            }
            inputMode="numeric"
            helperText="정원에 도달하거나 초과하면 배정 시 경고를 표시합니다. 저장은 허용하며 대기 명단은 만들지 않습니다."
            fullWidth
          />
          {formError && (
            <Typography role="alert" variant="body2" sx={{ color: colors.status.danger.fg }}>
              {formError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Classes;
